import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ConversationList from '../components/messaging/ConversationList';
import MessageThread from '../components/messaging/MessageThread';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';

const MessagesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Check if we should use dashboard layout (for user and trainer routes)
  const useDashboardLayout = location.pathname.startsWith('/user/messages') || location.pathname.startsWith('/trainer/messages');

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const messageContent = (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-black">
          Messages
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Your conversations and messages</p>
      </div>

      <Card className="h-[600px] border-2 border-red-200">
        <div className="flex h-full">
          {/* Conversations Sidebar */}
          <div className="w-1/3 border-r">
            <CardHeader>
              <CardTitle className="text-lg">Conversations</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto" style={{ maxHeight: 'calc(600px - 80px)' }}>
              {loading ? (
                <p className="text-center py-8 text-gray-500">Loading...</p>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">No conversations yet</p>
                  <p className="text-sm text-gray-400">Start a conversation with a trainer!</p>
                </div>
              ) : (
                <ConversationList
                  conversations={conversations}
                  selectedId={selectedConversationId}
                  onSelect={setSelectedConversationId}
                  currentUserId={user?.id}
                />
              )}
            </CardContent>
          </div>

          {/* Message Thread */}
          <div className="flex-1 flex flex-col">
            {selectedConversationId ? (
              <MessageThread
                conversationId={selectedConversationId}
                currentUserId={user?.id}
                conversation={conversations.find(c => c.id === selectedConversationId)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <p className="text-lg mb-2">Select a conversation to start messaging</p>
                  <p className="text-sm">Choose from your conversations on the left</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );

  // If we're on /user/messages, wrap with DashboardLayout
  if (useDashboardLayout) {
    return <DashboardLayout>{messageContent}</DashboardLayout>;
  }

  // Otherwise, use standalone layout
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="container mx-auto px-4">
        {messageContent}
      </div>
    </div>
  );
};

export default MessagesPage;
